import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Avatar } from '../components/Avatar';
import { Ic } from '../components/Icon';
import { roomsApi } from '../api/rooms';
import { roomInvitesApi } from '../api/roomInvites';
import { roomNotificationsApi } from '../api/roomNotifications';
import { useAuth } from '../store/auth';
import type { Room } from '../api/types';

export function MyRoomsPage() {
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const myRooms = useQuery({ queryKey: ['my-rooms'], queryFn: roomsApi.mine });
  const owned = myRooms.data?.owned ?? [];
  const member = myRooms.data?.member ?? [];

  const myInvites = useQuery({ queryKey: ['my-room-invites'], queryFn: roomInvitesApi.mine });
  const acceptInvite = useMutation({
    mutationFn: (id: string) => roomInvitesApi.accept(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-room-invites'] });
      queryClient.invalidateQueries({ queryKey: ['my-room-invites-count'] });
      queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
    },
  });
  const rejectInvite = useMutation({
    mutationFn: (id: string) => roomInvitesApi.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-room-invites'] });
      queryClient.invalidateQueries({ queryKey: ['my-room-invites-count'] });
    },
  });

  const myNotifications = useQuery({ queryKey: ['my-room-notifications'], queryFn: roomNotificationsApi.mine });
  const dismissNotif = useMutation({
    mutationFn: (id: string) => roomNotificationsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-room-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['my-room-notifications-count'] });
    },
  });

  const [search, setSearch] = useState('');
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const discover = useQuery({
    queryKey: ['discover', search],
    queryFn: () => roomsApi.discover(search, 60),
  });
  const requestJoin = useMutation({
    mutationFn: (roomId: string) => roomsApi.requestJoin(roomId),
    onSuccess: (_, roomId) => setRequested((s) => ({ ...s, [roomId]: true })),
  });

  return (
    <DesktopShell>
      <TopBar
        title="Moje pokoje"
        subtitle={`${owned.length} jako właściciel · ${member.length} jako uczestnik`}
      />
      <div className="responsive-pad" style={{ flex: 1, padding: '24px 36px 40px' }}>
        {(myNotifications.data ?? []).length > 0 && (
          <Section title="Powiadomienia" icon={<Ic.bell size={15} />} accent count={myNotifications.data?.length ?? 0}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {myNotifications.data!.map((n) => (
                <Card key={n.id} padding={14}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'rgba(208,70,60,.12)',
                        color: '#8a2a22',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Ic.logout size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>
                        {n.actorNick ?? 'Właściciel'} usunął/a Cię z pokoju <b>{n.roomName}</b>
                      </div>
                      <div style={{ fontSize: 12, color: '#5b6578' }}>
                        {new Date(n.createdAt).toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    </div>
                    <Btn
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissNotif.mutate(n.id)}
                      disabled={dismissNotif.isPending}
                    >
                      OK
                    </Btn>
                  </div>
                </Card>
              ))}
            </div>
          </Section>
        )}
        {(myInvites.data ?? []).length > 0 && (
          <Section title="Zaproszenia do pokojów" icon={<Ic.mail size={15} />} accent count={myInvites.data?.length ?? 0}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {myInvites.data!.map((inv) => (
                <Card key={inv.id} padding={14}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar nick={inv.inviterNick ?? '?'} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>
                        {inv.inviterNick ?? '—'} zaprasza Cię do <b>{inv.roomName}</b>
                      </div>
                      <div style={{ fontSize: 12, color: '#5b6578' }}>
                        Zaproszenie wygasa {new Date(inv.expiresAt).toLocaleDateString('pl-PL')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn
                        variant="ghost"
                        size="sm"
                        onClick={() => rejectInvite.mutate(inv.id)}
                        disabled={rejectInvite.isPending || acceptInvite.isPending}
                      >
                        Odrzuć
                      </Btn>
                      <Btn
                        variant="accent"
                        size="sm"
                        icon={<Ic.check size={13} />}
                        onClick={() => acceptInvite.mutate(inv.id)}
                        disabled={acceptInvite.isPending || rejectInvite.isPending}
                      >
                        Akceptuj
                      </Btn>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Section>
        )}
        <Section
          title="Pokoje, które prowadzisz"
          icon={<Ic.trophy size={15} />}
          accent
          count={owned.length}
        >
          {owned.length === 0 ? (
            <Card padding={20} style={{ background: '#f7f6f2', border: '1.5px dashed rgba(15,30,61,.16)' }}>
              <div style={{ textAlign: 'center', color: '#5b6578' }}>
                <p style={{ marginBottom: 12 }}>Nie utworzyłeś jeszcze żadnego pokoju.</p>
                <Link to="/rooms/new">
                  <Btn variant="accent">Załóż pierwszy pokój</Btn>
                </Link>
              </div>
            </Card>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 14,
              }}
            >
              {owned.map((r) => (
                <OwnedCard key={r.id} room={r} isMine={r.ownerId === user?.id} />
              ))}
              <Link to="/rooms/new" style={{ textDecoration: 'none' }}>
                <Card
                  padding={20}
                  style={{
                    background: '#f7f6f2',
                    border: '1.5px dashed rgba(15,30,61,.16)',
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: '#fff',
                        margin: '0 auto 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent)',
                      }}
                    >
                      <Ic.plus size={22} />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Nowy pokój</div>
                    <div
                      style={{ fontSize: 12, color: '#5b6578', marginTop: 4, maxWidth: 180, lineHeight: 1.4 }}
                    >
                      Prywatny dla znajomych albo publiczny dla wszystkich.
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          )}
        </Section>

        <Section
          title="Pokoje, do których należysz"
          icon={<Ic.users size={15} />}
          count={member.length}
        >
          {member.length === 0 ? (
            <Card padding={18} style={{ color: '#5b6578', textAlign: 'center' }}>
              Nie należysz jeszcze do żadnego cudzego pokoju. Znajdź pokój poniżej.
            </Card>
          ) : (
            <Card padding={0}>
              {member.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/rooms/${r.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 22px',
                    borderBottom: i === member.length - 1 ? 'none' : '1px solid rgba(15,30,61,.04)',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 9,
                      background: '#f4f3ee',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {r.emoji ?? '🏆'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                      <Pill color="gray">
                        {r.type === 'PRIVATE' ? <Ic.lock size={9} /> : <Ic.globe size={9} />}
                      </Pill>
                    </div>
                  </div>
                  <Btn variant="ghost" size="sm">
                    Otwórz <Ic.chevR size={11} />
                  </Btn>
                </Link>
              ))}
            </Card>
          )}
        </Section>

        <Section
          title="Odkryj pokoje"
          icon={<Ic.search size={15} />}
          count={discover.data?.length ?? 0}
        >
          <Card padding={14} style={{ marginBottom: 14 }}>
            <div style={{ position: 'relative' }}>
              <Ic.search
                size={16}
                style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#5b6578' }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj po nazwie pokoju…"
                style={{
                  width: '100%',
                  height: 42,
                  borderRadius: 10,
                  border: '1px solid rgba(15,30,61,.1)',
                  padding: '0 14px 0 40px',
                  fontSize: 14,
                  background: '#f7f6f2',
                  outline: 'none',
                }}
              />
            </div>
          </Card>

          {discover.isLoading && <Card padding={18}>Ładowanie…</Card>}
          {!discover.isLoading && discover.data?.length === 0 && (
            <Card padding={18} style={{ background: '#f7f6f2', textAlign: 'center', color: '#5b6578' }}>
              {search ? 'Brak publicznych pokoi pasujących do wyszukiwania.' : 'Brak publicznych pokoi.'}
            </Card>
          )}

          {!discover.isLoading && (discover.data?.length ?? 0) > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 14,
              }}
            >
              {(discover.data ?? []).map((r) => (
                <DiscoverCard
                  key={r.id}
                  room={r}
                  isOwner={r.ownerId === user?.id}
                  isMember={member.some((m) => m.id === r.id)}
                  requested={!!requested[r.id]}
                  onRequest={() => requestJoin.mutate(r.id)}
                />
              ))}
            </div>
          )}
        </Section>
      </div>
    </DesktopShell>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  accent?: boolean;
  count: number;
  children: React.ReactNode;
}

function Section({ title, icon, accent, count, children }: SectionProps) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: accent ? 'var(--accent)' : 'rgba(15,30,61,.08)',
            color: accent ? '#3a2a00' : '#0f1e3d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <h3 className="serif" style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
          {title}
        </h3>
        <Pill color={accent ? 'accent' : 'gray'}>{count}</Pill>
      </div>
      {children}
    </div>
  );
}

function OwnedCard({ room }: { room: Room; isMine: boolean }) {
  return (
    <Card
      padding={0}
      style={{
        overflow: 'hidden',
        borderTop: '3px solid var(--accent)',
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 11,
              background: '#f4f3ee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            {room.emoji ?? '🏆'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {room.name}
              </div>
              <div className="num" style={{ fontSize: 12, color: '#5b6578', flexShrink: 0 }}>
                {room.memberCount}/{room.maxMembers}
              </div>
            </div>
            <div style={{ marginTop: 4 }}>
              <Pill color="gray">
                {room.type === 'PRIVATE' ? <Ic.lock size={9} /> : <Ic.globe size={9} />}
                {room.type === 'PRIVATE' ? 'prywatny' : 'publiczny'}
              </Pill>
            </div>
          </div>
        </div>

        {room.description && (
          <div
            style={{
              fontSize: 12,
              color: '#5b6578',
              lineHeight: 1.4,
              marginBottom: 12,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {room.description}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
          <Link to={`/rooms/${room.id}`} style={{ flex: 1, textDecoration: 'none' }}>
            <Btn variant="primary" size="sm" fullWidth>
              Otwórz
            </Btn>
          </Link>
          <Link to={`/rooms/${room.id}/manage`} style={{ flex: 1, textDecoration: 'none' }}>
            <Btn variant="ghost" size="sm" fullWidth icon={<Ic.edit size={12} />}>
              Zarządzaj
            </Btn>
          </Link>
        </div>
      </div>
    </Card>
  );
}

function DiscoverCard({
  room,
  isOwner,
  isMember,
  requested,
  onRequest,
}: {
  room: Room;
  isOwner: boolean;
  isMember: boolean;
  requested: boolean;
  onRequest: () => void;
}) {
  return (
    <Card
      padding={18}
      style={{ minHeight: 200, display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 11,
            background: '#f4f3ee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          {room.emoji ?? '🏆'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.005em',
                minWidth: 0,
              }}
            >
              {room.name}
            </div>
            <div className="num" style={{ fontSize: 12, color: '#5b6578', flexShrink: 0 }}>
              {room.memberCount}/{room.maxMembers}
            </div>
          </div>
          <div style={{ marginTop: 4 }}>
            <Pill color="gray">
              {room.type === 'PRIVATE' ? <Ic.lock size={9} /> : <Ic.globe size={9} />}
              {room.type === 'PRIVATE' ? 'prywatny' : 'publiczny'}
            </Pill>
          </div>
        </div>
      </div>

      {room.description && (
        <div
          style={{
            fontSize: 12,
            color: '#5b6578',
            lineHeight: 1.4,
            marginBottom: 12,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {room.description}
        </div>
      )}

      <div style={{ marginTop: 'auto' }}>
        {isOwner ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 38,
              borderRadius: 19,
              background: 'rgba(244,185,58,.18)',
              color: '#6b4a00',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Ic.trophy size={13} />
            Jesteś właścicielem
          </div>
        ) : isMember ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 38,
              borderRadius: 19,
              background: 'rgba(31,157,85,.12)',
              color: '#0f6b34',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Ic.check size={13} />
            Już należysz do pokoju
          </div>
        ) : (
          <Btn variant={requested ? 'subtle' : 'ghost'} size="md" fullWidth onClick={onRequest} disabled={requested}>
            {requested ? (
              <>
                Prośba wysłana <Ic.check size={13} />
              </>
            ) : (
              <>
                Poproś o dołączenie <Ic.chevR size={12} />
              </>
            )}
          </Btn>
        )}
      </div>
    </Card>
  );
}
